import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, Download, FileSpreadsheet } from "lucide-react";
import { api, errMsg } from "@/lib/api";

export const MAX_BATCH = 6;

export const PhotoUploadButton = ({ uploadUrl, attachUrl, count, max, onDone, label, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState("");
  const pick = async (fileList) => {
    let files = Array.from(fileList || []);
    if (!files.length) return;
    if (files.length > MAX_BATCH) { toast.error(`Massimo ${MAX_BATCH} foto alla volta: carico le prime ${MAX_BATCH}`); files = files.slice(0, MAX_BATCH); }
    if (max && count + files.length > max) {
      if (count >= max) return toast.error(`Hai già ${max} foto: limite raggiunto`);
      toast.error(`Massimo ${max} foto: carico le prime ${max - count}`);
      files = files.slice(0, max - count);
    }
    const urls = [];
    try {
      for (let i = 0; i < files.length; i += 1) {
        setBusy(`${i + 1}/${files.length}`);
        const fd = new FormData();
        fd.append("file", files[i]);
        urls.push((await api.post(uploadUrl, fd)).data.url);
      }
      await api.post(attachUrl, { urls });
      toast.success(`${urls.length} foto aggiunte`);
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  return (
    <>
      <input ref={ref} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { pick(e.target.files); e.target.value = ""; }} data-testid={`${testid}-input`} />
      <button type="button" disabled={!!busy} onClick={() => ref.current.click()} data-testid={testid}
        className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#EFE9FB] px-3 py-1.5 text-xs font-semibold text-[#3B1272] hover:bg-[#E2D8F7] disabled:opacity-60">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}{busy || `${label} (${count})`}
      </button>
    </>
  );
};

export const downloadFile = async (url, name) => {
  try {
    const { data } = await api.get(url, { responseType: "blob" });
    const href = URL.createObjectURL(data);
    const a = document.createElement("a"); a.href = href; a.download = name; a.click(); URL.revokeObjectURL(href);
  } catch (e) { toast.error(errMsg(e)); }
};

export const ExcelImport = ({ templateUrl, templateName, importUrl, onDone, labels, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const pick = async (file) => {
    if (!file) return;
    setBusy(true); setRes(null);
    const fd = new FormData();
    fd.append("file", file);
    try { const { data } = await api.post(importUrl, fd); setRes(data); onDone?.(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="rounded-2xl border border-dashed border-[#C9B8EC] bg-[#FBF9FF] p-4" data-testid={testid}>
      <p className="flex items-center gap-2 font-semibold text-[#3B1272]"><FileSpreadsheet className="h-5 w-5" />{labels.title}</p>
      <p className="mt-1 text-xs text-[#64748B]">{labels.hint}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => downloadFile(templateUrl, templateName)} className="btn-ghost !py-2 text-sm" data-testid={`${testid}-template-btn`}><Download className="h-4 w-4" />{labels.template}</button>
        <input ref={ref} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} data-testid={`${testid}-input`} />
        <button type="button" disabled={busy} onClick={() => ref.current.click()} className="btn-violet !py-2 text-sm" data-testid={`${testid}-upload-btn`}>{busy && <Loader2 className="h-4 w-4 animate-spin" />}{labels.upload}</button>
      </div>
      {res && (
        <div className="mt-3 rounded-xl bg-white p-3 text-sm" data-testid={`${testid}-result`}>
          <p className="font-semibold text-emerald-700" data-testid={`${testid}-created`}>{labels.created}: {res.created}</p>
          {res.errors.length > 0 && <ul className="mt-1 max-h-40 space-y-0.5 overflow-y-auto text-xs text-red-700" data-testid={`${testid}-errors`}>
            {res.errors.map((e) => <li key={e.row}>{labels.row} {e.row}: {e.message}</li>)}</ul>}
        </div>
      )}
    </div>
  );
};
